import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Brain, Heart, Sparkles, Send, CircleSlash } from "lucide-react";

// Plano-15 Onda 8 — funil unificado dos 3 nós cognitivos do Cortex
// (ToM, Affect, DMN). Mostra, por nó: injetado no prompt vs realmente
// usado na resposta final. Sem placebo: cada número vem de eventos
// `*_injected` e `*_used_in_reply` reais em telemetry_events.

type Row = {
  org_id: string;
  tom_injected_24h: number;
  tom_skipped_24h: number;
  tom_used_24h: number;
  tom_not_used_24h: number;
  affect_injected_24h: number;
  affect_skipped_24h: number;
  affect_used_24h: number;
  affect_not_used_24h: number;
  dmn_injected_24h: number;
  dmn_used_24h: number;
  dmn_not_used_24h: number;
};

type Totals = Omit<Row, "org_id">;

const EMPTY: Totals = {
  tom_injected_24h: 0, tom_skipped_24h: 0, tom_used_24h: 0, tom_not_used_24h: 0,
  affect_injected_24h: 0, affect_skipped_24h: 0, affect_used_24h: 0, affect_not_used_24h: 0,
  dmn_injected_24h: 0, dmn_used_24h: 0, dmn_not_used_24h: 0,
};

interface NodeConfig {
  id: "tom" | "affect" | "dmn";
  label: string;
  description: string;
  icon: typeof Brain;
  injectedKey: keyof Totals;
  usedKey: keyof Totals;
  skippedKey?: keyof Totals;
}

const NODES: NodeConfig[] = [
  {
    id: "tom",
    label: "ToM",
    description: "Theory of Mind do interlocutor",
    icon: Brain,
    injectedKey: "tom_injected_24h",
    usedKey: "tom_used_24h",
    skippedKey: "tom_skipped_24h",
  },
  {
    id: "affect",
    label: "Affect",
    description: "Estado emocional do grupo (VAD)",
    icon: Heart,
    injectedKey: "affect_injected_24h",
    usedKey: "affect_used_24h",
    skippedKey: "affect_skipped_24h",
  },
  {
    id: "dmn",
    label: "DMN",
    description: "Insight contextual de divagação",
    icon: Sparkles,
    injectedKey: "dmn_injected_24h",
    usedKey: "dmn_used_24h",
  },
];

export default function CortexCognitiveFunnelCards() {
  const [totals, setTotals] = useState<Totals | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      const { data, error } = await supabase
        .from("cortex_cognitive_funnel_view" as any)
        .select("*");
      if (!mounted) return;
      if (error || !data) {
        setTotals(EMPTY);
        setLoading(false);
        return;
      }
      const sum: Totals = { ...EMPTY };
      for (const r of data as unknown as Row[]) {
        (Object.keys(EMPTY) as (keyof Totals)[]).forEach((k) => {
          sum[k] += Number(r[k] ?? 0);
        });
      }
      setTotals(sum);
      setLoading(false);
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const conv = (used: number, injected: number) =>
    injected === 0 ? "—" : `${Math.round((used / injected) * 100)}%`;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-medium">Funil cognitivo — últimas 24h</h3>
          <p className="text-xs text-muted-foreground">
            Cada nó: bloco injetado no prompt → realmente usado na resposta (gate ok + reply não-vazia)
          </p>
        </div>
        {!loading && totals && (
          <Badge variant="outline" className="font-mono text-[10px]">
            cortex_cognitive_funnel_view
          </Badge>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {NODES.map((node) => {
          const Icon = node.icon;
          const injected = totals?.[node.injectedKey] ?? 0;
          const used = totals?.[node.usedKey] ?? 0;
          const skipped = node.skippedKey ? totals?.[node.skippedKey] ?? 0 : null;
          const ratio = conv(used, injected);
          const tone =
            injected === 0
              ? "bg-muted text-muted-foreground"
              : used / Math.max(injected, 1) >= 0.6
                ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"
                : "bg-amber-500/15 text-amber-700 dark:text-amber-300";

          return (
            <Card key={node.id}>
              <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
                <div>
                  <CardTitle className="text-sm font-semibold">{node.label}</CardTitle>
                  <p className="text-[11px] text-muted-foreground mt-0.5">
                    {node.description}
                  </p>
                </div>
                <Icon className="w-4 h-4 text-muted-foreground" />
              </CardHeader>
              <CardContent className="space-y-3">
                {loading || !totals ? (
                  <Skeleton className="h-16 w-full" />
                ) : (
                  <>
                    <div className="flex items-end justify-between">
                      <div>
                        <div className="text-2xl font-bold tabular-nums">{used}</div>
                        <div className="text-[10px] text-muted-foreground">
                          usados na resposta
                        </div>
                      </div>
                      <Badge variant="outline" className={tone}>
                        {ratio}
                      </Badge>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-[11px]">
                      <div className="flex items-center gap-1.5 rounded border border-border/50 px-2 py-1">
                        <Send className="w-3 h-3 text-muted-foreground" />
                        <div>
                          <div className="font-medium text-foreground tabular-nums">
                            {injected}
                          </div>
                          <div className="text-muted-foreground text-[10px]">injetado</div>
                        </div>
                      </div>
                      {skipped !== null && (
                        <div className="flex items-center gap-1.5 rounded border border-border/50 px-2 py-1">
                          <CircleSlash className="w-3 h-3 text-muted-foreground" />
                          <div>
                            <div className="font-medium text-foreground tabular-nums">
                              {skipped}
                            </div>
                            <div className="text-muted-foreground text-[10px]">skipped</div>
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
